import { MappingMetadata } from 'api/RobotApi'
import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import { useUser } from 'hooks/user'
import { ReactComponent as ArrowSvg } from 'images/icons/arrow_select.svg'
import { ReactComponent as FirmwareUpdateSvg } from 'images/icons/firmware_update.svg'
import { ReactComponent as LocalizationSvg } from 'images/icons/location-bold.svg'
import { ReactComponent as MappingSvg } from 'images/icons/map-bold.svg'
import { ReactComponent as ParkingSvg } from 'images/icons/parking.svg'
import { ReactComponent as ChargingSvg } from 'images/icons/plug_in.svg'
import { ReactComponent as RoutingSvg } from 'images/icons/route.svg'
import { ReactComponent as WashingSvg } from 'images/icons/shower.svg'
import { ReactComponent as UpdateSvg } from 'images/icons/update.svg'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectRobot, selectRobotTelemetry } from 'store/selectors/robot'
import { selectRobotActiveMode } from 'store/selectors/robot'
import store from 'store/store'
import { robotThunks } from 'store/thunks/robot'
import { ROBOT_COMMANDS, ROBOT_MODES, ROBOT_NETWORK_STATUSES, USE_NEW_MAP_RECORDING_UI } from 'utils/constants'

import MapRecordingModal from './StartMapRecordingModal/MapRecordingModal'
import { MappingFormData } from './StartMapRecordingModal/MapRecordingView/types'

import style from './Service.module.css'

export enum LOCALIZATION_MODES {
  UNDEFINED = 'Undefined',
  LIDAR_LOCALIZATION = 'Lidar based',
  GNSS_BASED_LOCALIZATION = 'GNSS Based',
  COMBINED_LOCALIZATION = 'Combined',
}

export const localiazationModeMapping: Record<number, LOCALIZATION_MODES> = {
  0: LOCALIZATION_MODES.UNDEFINED,
  1: LOCALIZATION_MODES.LIDAR_LOCALIZATION,
  2: LOCALIZATION_MODES.GNSS_BASED_LOCALIZATION,
  3: LOCALIZATION_MODES.COMBINED_LOCALIZATION,
}

const localiazationModeToRobotModesMapping: Record<LOCALIZATION_MODES, ROBOT_MODES> = {
  [LOCALIZATION_MODES.UNDEFINED]: ROBOT_MODES.MODE_NOT_SET,
  [LOCALIZATION_MODES.LIDAR_LOCALIZATION]: ROBOT_MODES.MODE_SWITCHING_TO_LIDAR_LOCALIZATION,
  [LOCALIZATION_MODES.GNSS_BASED_LOCALIZATION]: ROBOT_MODES.MODE_SWITCHING_TO_GNSS_BASED_LOCALIZATION,
  [LOCALIZATION_MODES.COMBINED_LOCALIZATION]: ROBOT_MODES.MODE_SWITCHING_TO_COMBINED_LOCALIZATION,
}

const Service = () => {
  const activeMode = useSelector(selectRobotActiveMode)
  const robot = useSelector(selectRobot)
  const { t } = useTranslation()
  const telemetry = useSelector(selectRobotTelemetry)
  const { state: userState } = useUser() ?? { state: { isSuperuser: false, isStaff: false } }
  const canSeeRebootButton = userState.isSuperuser || userState.isStaff

  const [confirmParkingDialog, setConfirmParkingDialog] = useState(false)
  const [confirmChargingDialog, setConfirmChargingDialog] = useState(false)
  const [confirmWashingDialog, setConfirmWashingDialog] = useState(false)
  const [confirmStartMappingDialog, setConfirmStartMappingDialog] = useState(false)
  const [confirmStartRoutingDialog, setConfirmStartRoutingDialog] = useState(false)
  const [confirmCalibParkingDialog, setConfirmCalibParkingDialog] = useState(false)
  const [confirmCalibChargingDialog, setConfirmCalibChargingDialog] = useState(false)
  const [confirmCalibWashingDialog, setConfirmCalibWashingDialog] = useState(false)
  const [confirmSwitchLocalizationDialog, setConfirmSwitchLocalizationDialog] = useState(false)
  const [confirmUpdatingFirmwareDialog, setConfirmUpdatingFirmwareDialog] = useState(false)
  const [confirmRebootDialog, setConfirmRebootDialog] = useState(false)
  const [showAdditionalSettings, setShowAdditionalSettings] = useState(false)
  const [selectedLocalizationMode, setSelectedLocalizationMode] = useState(LOCALIZATION_MODES.UNDEFINED)
  const [currentFirmwareVersion, setCurrentFirmwareVersion] = useState<string>('')
  const [latestFirmwareVersion, setLatestFirmwareVersion] = useState<string>('')
  const [currentLocalizationMode, setCurrentLocalizationMode] = useState(LOCALIZATION_MODES.UNDEFINED)

  const isOnline = robot.robotInfo?.networkStatus === ROBOT_NETWORK_STATUSES.ONLINE

  const modeInProgress = activeMode !== ROBOT_MODES.MODE_NOT_SET && activeMode !== ROBOT_MODES.MODE_MANUAL
  const modeInParking = activeMode === ROBOT_MODES.MODE_PARKING
  const modeInCharging = activeMode === ROBOT_MODES.MODE_CHARGING
  const modeInWashing = activeMode === ROBOT_MODES.MODE_WASHING
  const modeInMapping = activeMode === ROBOT_MODES.MODE_MAPPING
  const modeInRouteRecording = activeMode === ROBOT_MODES.MODE_ROUTE_RECORDING
  const modeInCalibParking = activeMode === ROBOT_MODES.MODE_CALIBRATION_PARKING
  const modeInCalibCharging = activeMode === ROBOT_MODES.MODE_CALIBRATION_CHARGING
  const modeInCalibWashing = activeMode === ROBOT_MODES.MODE_CALIBRATION_WASHING
  const modeSwitchLocalization =
    activeMode === ROBOT_MODES.MODE_SWITCHING_TO_COMBINED_LOCALIZATION ||
    activeMode === ROBOT_MODES.MODE_SWITCHING_TO_GNSS_BASED_LOCALIZATION ||
    activeMode === ROBOT_MODES.MODE_SWITCHING_TO_LIDAR_LOCALIZATION
  const modeUpdatingFirmware = activeMode === ROBOT_MODES.MODE_UPDATING_FIRMWARE

  const isRobotOnMission = activeMode === ROBOT_MODES.MODE_AUTOMATIC && Boolean(robot.robotInfo?.job)

  const canRebootRobot =
    isOnline && (!isRobotOnMission || (robot.robotInfo?.activeCommand === ROBOT_COMMANDS.PAUSE && isRobotOnMission))

  const getRebootStatusText = () => {
    if (!isOnline) {
      return 'Robot offline'
    }
    if (canRebootRobot) return 'Ready to reboot'

    return 'Reboot is available when mission is paused or without mision'
  }

  const localizationModeOptions: IOption[] = Object.values(LOCALIZATION_MODES)
    .filter(value => typeof value === 'string' && value !== LOCALIZATION_MODES.UNDEFINED)
    .map(mode => ({
      text: mode,
      value: mode,
    }))

  const [localizationModeOption, setLocalizationModeOption] = useState(
    selectedLocalizationMode
      ? { text: selectedLocalizationMode, value: selectedLocalizationMode }
      : localizationModeOptions[2]
  )

  useEffect(() => {
    if (telemetry?.firmware_info) {
      setCurrentFirmwareVersion(telemetry.firmware_info.current_version)
      setLatestFirmwareVersion(telemetry.firmware_info.latest_version)
    }

    if (telemetry?.localization_info) {
      const currentMode = localiazationModeMapping[telemetry.localization_info.localization_mode] as LOCALIZATION_MODES
      setCurrentLocalizationMode(currentMode)

      if (selectedLocalizationMode === LOCALIZATION_MODES.UNDEFINED) {
        const selectedOption = localizationModeOptions.find(option => option.value === currentMode)
        if (selectedOption) {
          setLocalizationModeOption(selectedOption)
          setSelectedLocalizationMode(currentMode)
        }
      }
    }
  }, [telemetry])

  const onLocalizationModeSelected = (e: IOption) => {
    setLocalizationModeOption(e)
    setSelectedLocalizationMode(e.value as LOCALIZATION_MODES)
  }

  const handleModeClick = (mode: boolean, targetMode: ROBOT_MODES) => {
    if (robot.robotInfo?.id !== undefined) {
      if (!mode) {
        store.dispatch(robotThunks.setRobotMode(robot.robotInfo.id, targetMode))
      } else {
        store.dispatch(robotThunks.setRobotNotSetMode(robot.robotInfo.id))
      }
    }
  }

  const onSetParkingModeClicked = () => {
    handleModeClick(modeInParking, ROBOT_MODES.MODE_PARKING)
    setConfirmParkingDialog(false)
  }
  const onSetChargingModeClicked = () => {
    handleModeClick(modeInCharging, ROBOT_MODES.MODE_CHARGING)
    setConfirmChargingDialog(false)
  }
  const onSetWashingModeClicked = () => {
    handleModeClick(modeInWashing, ROBOT_MODES.MODE_WASHING)
    setConfirmWashingDialog(false)
  }
  const onSetCalibParkingModeClicked = () => {
    handleModeClick(modeInCalibParking, ROBOT_MODES.MODE_CALIBRATION_PARKING)
    setConfirmCalibParkingDialog(false)
  }
  const onSetCalibChargingModeClicked = () => {
    handleModeClick(modeInCalibCharging, ROBOT_MODES.MODE_CALIBRATION_CHARGING)
    setConfirmCalibChargingDialog(false)
  }
  const onSetCalibWashingModeClicked = () => {
    handleModeClick(modeInCalibWashing, ROBOT_MODES.MODE_CALIBRATION_WASHING)
    setConfirmCalibWashingDialog(false)
  }
  // Convert yaw from UI format (0 = north, range [0, 360)) to API format (0 = east, range (-180, 180])
  const convertYawToApi = (uiYaw: number): number => {
    // UI: 0 = north, API: 0 = east
    // East in UI = 90°, so subtract 90
    let apiYaw = uiYaw - 90

    // Normalize to range (-180, 180]
    while (apiYaw > 180) apiYaw -= 360
    while (apiYaw <= -180) apiYaw += 360

    return apiYaw
  }

  const buildMappingMetadata = (formData: MappingFormData): MappingMetadata | undefined => {
    // Get localization mode from telemetry
    const localizationMode = telemetry?.localization_info?.localization_mode
      ? localiazationModeMapping[telemetry.localization_info.localization_mode]
      : LOCALIZATION_MODES.UNDEFINED
    const isGnssMode = localizationMode === LOCALIZATION_MODES.GNSS_BASED_LOCALIZATION

    switch (formData.subMode) {
      case 'update_existing':
        if (!formData.selectedLocationName) {
          return undefined
        }
        return {
          name: formData.selectedLocationName,
          update_map: true,
        }

      case 'new_location':
        if (!formData.locationName?.trim()) {
          return undefined
        }
        return {
          name: formData.locationName.trim(),
          continue_on_the_facility: true,
        }

      case 'new_facility':
        if (!formData.locationName?.trim()) {
          return undefined
        }

        // GNSS mode: no position/orientation required
        if (isGnssMode) {
          return {
            name: formData.locationName.trim(),
            update_map: false,
            continue_on_the_facility: false,
          }
        }

        // Non-GNSS mode: position and orientation are required
        if (!formData.robotPosition || formData.robotYaw === undefined) {
          return undefined
        }

        const [lon, lat] = formData.robotPosition.coordinates
        const apiYaw = convertYawToApi(formData.robotYaw)
        return {
          name: formData.locationName.trim(),
          pose: {
            lon,
            lat,
            yaw: apiYaw,
          },
          update_map: false,
          continue_on_the_facility: false,
        }

      case 'simple_legacy':
        // Simple legacy mode: no metadata needed, will use skip_metadata flag
        return undefined
    }
  }

  // Old mapping mode handler (without metadata) - used when USE_NEW_MAP_RECORDING_UI is false
  const onSetMappingModeClickedOld = () => {
    handleModeClick(modeInMapping, ROBOT_MODES.MODE_MAPPING)
    setConfirmStartMappingDialog(false)
  }

  // New mapping mode handler (with metadata) - used when USE_NEW_MAP_RECORDING_UI is true
  const onSetMappingModeClicked = async (formData: MappingFormData) => {
    if (robot.robotInfo?.id !== undefined) {
      // For simple_legacy mode, don't send metadata (empty body = legacy mode)
      // For other modes, build and send metadata
      const metadata = formData.subMode === 'simple_legacy' ? undefined : buildMappingMetadata(formData)

      if (metadata !== undefined || formData.subMode === 'simple_legacy') {
        try {
          await store.dispatch(robotThunks.setRobotMode(robot.robotInfo.id, ROBOT_MODES.MODE_MAPPING, metadata))
        } catch (error) {
          // Error is handled upstream (e.g., toast notifications); no console spam needed.
        }
      }
    }
    setConfirmStartMappingDialog(false)
  }

  const onStopMappingModeClicked = () => {
    handleModeClick(modeInMapping, ROBOT_MODES.MODE_MAPPING)
  }
  const onSetRouteRecordingModeClicked = () => {
    handleModeClick(modeInRouteRecording, ROBOT_MODES.MODE_ROUTE_RECORDING)
    setConfirmStartRoutingDialog(false)
  }
  const onSwitchLocalizationClicked = () => {
    handleModeClick(modeSwitchLocalization, localiazationModeToRobotModesMapping[selectedLocalizationMode])
    setConfirmSwitchLocalizationDialog(false)
  }

  const onUpdateFirmwareClicked = () => {
    handleModeClick(modeUpdatingFirmware, ROBOT_MODES.MODE_UPDATING_FIRMWARE)
    setConfirmUpdatingFirmwareDialog(false)
  }

  const onRebootRobotClicked = () => {
    if (robot.robotInfo?.id !== undefined) {
      store.dispatch(robotThunks.setRobotMode(robot.robotInfo.id, ROBOT_MODES.MODE_REBOOT_ROBOT))
    }
    setConfirmRebootDialog(false)
  }

  return (
    <div className={style.root}>
      <div className={style.tableHeaderRow}>
        <div className={style.centered}>Maintenance Tasks</div>
      </div>

      <div className={style.tableRow}>
        <div className={style.centered}>
          <ParkingSvg />
        </div>
        <div className={clsx(style.chip, style.centered, style.locked)}>
          <Button.Standart
            className={style.button}
            disabled={modeInProgress && !modeInParking}
            onClick={() => (modeInParking ? onSetParkingModeClicked() : setConfirmParkingDialog(true))}
          >
            {!modeInParking ? 'Start Parking' : 'Stop Parking'}
          </Button.Standart>
        </div>
        <div className={clsx(style.chip, style.centered, style.locked)}>
          <Button.Outlined
            className={style.buttonSmall}
            disabled={modeInProgress && !modeInCalibParking}
            onClick={() => (modeInCalibParking ? onSetCalibParkingModeClicked() : setConfirmCalibParkingDialog(true))}
          >
            {!modeInCalibParking ? 'Start calibration' : 'Stop Calibration'}
          </Button.Outlined>
        </div>
      </div>

      <div className={style.tableRow}>
        <div className={style.centered}>
          <ChargingSvg />
        </div>
        <div className={clsx(style.chip, style.centered, style.locked)}>
          <Button.Standart
            className={style.button}
            disabled={modeInProgress && !modeInCharging}
            onClick={() => (modeInCharging ? onSetChargingModeClicked() : setConfirmChargingDialog(true))}
          >
            {!modeInCharging ? 'Start Charging' : 'Stop Charging'}
          </Button.Standart>
        </div>
        <div className={clsx(style.chip, style.centered, style.locked)}>
          <Button.Outlined
            className={style.buttonSmall}
            disabled={modeInProgress && !modeInCalibCharging}
            onClick={() =>
              modeInCalibCharging ? onSetCalibChargingModeClicked() : setConfirmCalibChargingDialog(true)
            }
          >
            {!modeInCalibCharging ? 'Start calibration' : 'Stop Calibration'}
          </Button.Outlined>
        </div>
      </div>

      <div className={style.tableRow}>
        <div className={style.centered}>
          <WashingSvg />
        </div>
        <div className={clsx(style.chip, style.centered, style.locked)}>
          <Button.Standart
            className={style.button}
            disabled={modeInProgress && !modeInWashing}
            onClick={() => (modeInWashing ? onSetWashingModeClicked() : setConfirmWashingDialog(true))}
          >
            {!modeInWashing ? 'Start Washing' : 'Stop Washing'}
          </Button.Standart>
        </div>
        <div className={clsx(style.chip, style.centered, style.locked)}>
          <Button.Outlined
            className={style.buttonSmall}
            disabled={modeInProgress && !modeInCalibWashing}
            onClick={() => (modeInCalibWashing ? onSetCalibWashingModeClicked() : setConfirmCalibWashingDialog(true))}
          >
            {!modeInCalibWashing ? 'Start calibration' : 'Stop Calibration'}
          </Button.Outlined>
        </div>
      </div>

      <div className={style.tableHeaderRow}>
        <div className={style.centered}>Mapping Tasks</div>
      </div>

      <div className={style.tableRow}>
        <div className={style.centered}>
          <MappingSvg />
        </div>
        <div className={clsx(style.chip, style.centered, style.locked)}>
          <Button.Standart
            className={style.button}
            disabled={
              (modeInProgress && !modeInMapping) ||
              (!modeInMapping && robot.robotInfo?.networkStatus === ROBOT_NETWORK_STATUSES.OFFLINE)
            }
            onClick={() => (modeInMapping ? onStopMappingModeClicked() : setConfirmStartMappingDialog(true))}
          >
            {!modeInMapping ? 'Start Map Recording' : 'Stop Map Recording'}
          </Button.Standart>
        </div>
        <div className={clsx(style.chip, style.centered, style.locked)}>
          {modeInMapping ? 'Mapping in progress...' : ''}
        </div>
      </div>

      <div className={style.tableRow}>
        <div className={style.centered}>
          <RoutingSvg />
        </div>
        <div className={clsx(style.chip, style.centered, style.locked)}>
          <Button.Standart
            className={style.button}
            disabled={modeInProgress && !modeInRouteRecording}
            onClick={() =>
              modeInRouteRecording ? onSetRouteRecordingModeClicked() : setConfirmStartRoutingDialog(true)
            }
          >
            {!modeInRouteRecording ? 'Start Route Recording' : 'Stop Route Recording'}
          </Button.Standart>
        </div>
        <div className={clsx(style.chip, style.centered, style.locked)}>
          {modeInRouteRecording ? 'Route recording in progress...' : ''}
        </div>
      </div>

      <div className={style.additionalInfoHeader}>
        <div>{t('robot.additional_settings')}</div>
        <div className={style.showMore} onClick={() => setShowAdditionalSettings(!showAdditionalSettings)}>
          {showAdditionalSettings ? t('robot.additional_info_hide') : t('robot.additional_info_show')}
          <ArrowSvg className={style.icon} />
        </div>
      </div>
      {showAdditionalSettings && (
        <>
          <div className={style.tableRow}>
            <div className={style.centered}>
              <LocalizationSvg />
            </div>
            <div className={clsx(style.chip, style.centered, style.locked)}>
              <Button.Standart
                className={style.button}
                disabled={
                  modeInProgress ||
                  modeSwitchLocalization ||
                  selectedLocalizationMode === LOCALIZATION_MODES.UNDEFINED ||
                  currentLocalizationMode === selectedLocalizationMode ||
                  selectedLocalizationMode === LOCALIZATION_MODES.COMBINED_LOCALIZATION
                }
                onClick={() => setConfirmSwitchLocalizationDialog(true)}
              >
                {selectedLocalizationMode === LOCALIZATION_MODES.COMBINED_LOCALIZATION
                  ? 'Comming soon'
                  : !modeSwitchLocalization
                  ? 'Switch localization mode'
                  : 'Switching localization in progress'}
              </Button.Standart>
            </div>
            <div className={clsx(style.chip, style.centered, style.locked)}>
              <Dropdown
                className={style.textfield}
                currentOption={localizationModeOption}
                options={localizationModeOptions}
                onClickOption={onLocalizationModeSelected}
              />
            </div>
          </div>

          <div className={style.tableRow}>
            <div className={style.centered}>
              <FirmwareUpdateSvg />
            </div>
            <div className={clsx(style.chip, style.centered, style.locked)}>
              <Button.Standart
                className={style.button}
                disabled={modeInProgress || modeUpdatingFirmware || currentFirmwareVersion === latestFirmwareVersion}
                onClick={() => setConfirmUpdatingFirmwareDialog(true)}
              >
                {!modeUpdatingFirmware ? "Update the robot's firmware" : 'Firmware is updating'}
              </Button.Standart>
            </div>
            <div className={clsx(style.chipSmallFont, style.centered, style.locked)}>
              {currentFirmwareVersion !== latestFirmwareVersion ? (
                <>
                  Firmware version: {currentFirmwareVersion} <br /> Update available: {latestFirmwareVersion}
                </>
              ) : (
                `Firmware version: ${currentFirmwareVersion}`
              )}
            </div>
          </div>

          {canSeeRebootButton && (
            <div className={style.tableRow}>
              <div className={style.centered}>
                <UpdateSvg />
              </div>
              <div className={clsx(style.chip, style.centered, style.locked)}>
                <Button.Standart
                  className={style.button}
                  disabled={!canRebootRobot}
                  onClick={() => setConfirmRebootDialog(true)}
                >
                  Reboot robot
                </Button.Standart>
              </div>
              <div className={clsx(style.chipSmallFont, style.centered, style.locked)}>{getRebootStatusText()}</div>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        title={'Start parking?'}
        isOpen={confirmParkingDialog}
        onClose={() => setConfirmParkingDialog(false)}
        onConfirmClick={onSetParkingModeClicked}
        confirmText={'Confirm'}
        danger={false}
      />

      <ConfirmDialog
        title={'Start charging?'}
        isOpen={confirmChargingDialog}
        onClose={() => setConfirmChargingDialog(false)}
        onConfirmClick={onSetChargingModeClicked}
        confirmText={'Confirm'}
        danger={false}
      />

      <ConfirmDialog
        title={'Start washing?'}
        isOpen={confirmWashingDialog}
        onClose={() => setConfirmWashingDialog(false)}
        onConfirmClick={onSetWashingModeClicked}
        confirmText={'Confirm'}
        danger={false}
      />

      {USE_NEW_MAP_RECORDING_UI ? (
        <MapRecordingModal
          isOpen={confirmStartMappingDialog}
          onClose={() => setConfirmStartMappingDialog(false)}
          onConfirm={onSetMappingModeClicked}
          robotInfo={robot.robotInfo}
        />
      ) : (
        <ConfirmDialog
          title={'Start map recording?'}
          isOpen={confirmStartMappingDialog}
          onClose={() => setConfirmStartMappingDialog(false)}
          onConfirmClick={onSetMappingModeClickedOld}
          confirmText={'Confirm'}
          danger={false}
        />
      )}

      <ConfirmDialog
        title={'Start route recording?'}
        isOpen={confirmStartRoutingDialog}
        onClose={() => setConfirmStartRoutingDialog(false)}
        onConfirmClick={onSetRouteRecordingModeClicked}
        confirmText={'Confirm'}
        danger={false}
      />

      <ConfirmDialog
        title={'Start the parking lot position calibration?'}
        isOpen={confirmCalibParkingDialog}
        onClose={() => setConfirmCalibParkingDialog(false)}
        onConfirmClick={onSetCalibParkingModeClicked}
        confirmText={'Confirm'}
        danger={true}
      />

      <ConfirmDialog
        title={'Start the charging lot position calibration?'}
        isOpen={confirmCalibChargingDialog}
        onClose={() => setConfirmCalibChargingDialog(false)}
        onConfirmClick={onSetCalibChargingModeClicked}
        confirmText={'Confirm'}
        danger={true}
      />

      <ConfirmDialog
        title={'Start the washing lot position calibration?'}
        isOpen={confirmCalibWashingDialog}
        onClose={() => setConfirmCalibWashingDialog(false)}
        onConfirmClick={onSetCalibWashingModeClicked}
        confirmText={'Confirm'}
        danger={true}
      />

      <ConfirmDialog
        title={'Switching the localization mode will take several minutes'}
        isOpen={confirmSwitchLocalizationDialog}
        onClose={() => setConfirmSwitchLocalizationDialog(false)}
        onConfirmClick={() => onSwitchLocalizationClicked()}
        confirmText={'Confirm'}
        danger={true}
      />

      <ConfirmDialog
        title={"Updating the robot's firmware will take several minutes"}
        isOpen={confirmUpdatingFirmwareDialog}
        onClose={() => setConfirmUpdatingFirmwareDialog(false)}
        onConfirmClick={onUpdateFirmwareClicked}
        confirmText={'Confirm'}
        danger={true}
      />

      <ConfirmDialog
        title={'Reboot the robot?'}
        isOpen={confirmRebootDialog}
        onClose={() => setConfirmRebootDialog(false)}
        onConfirmClick={onRebootRobotClicked}
        confirmText={'Confirm'}
        danger={true}
      />
    </div>
  )
}

export default Service
